import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Code2,
  Shield,
  Zap,
  Brain,
  Clock,
  AlertTriangle,
  Target,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  FileText,
  BarChart3,
  Rocket,
  Zap as Lightning,
  Code,
  Database,
  GitBranch,
  Cpu,
  DollarSign,
  Globe
} from 'lucide-react';
import '../../styles/ServicePageCommon.css';
import './GCCService.css';

const GCCService = () => {
  const location = useLocation();
  const services = [
    { title: 'GCC as a Service', path: '/services/gcc', icon: Building2 },
    { title: 'Product Engineering', path: '/services/product-engineering', icon: Code2 },
    { title: 'Infrastructure & Security', path: '/services/infrastructure-security', icon: Shield },
    { title: 'Special Engagements', path: '/services/special-engagements', icon: Zap },
    { title: 'AI & Innovation', path: '/services/ai-innovation', icon: Brain }
  ];

  return (
    <div className="gcc-service-page">
      {/* Main Content */}
      <section className="service-content-section">
        <div className="service-header">
          <h1 className="service-title golden-gradient">GCC as a Service</h1>
          <p className="service-subtitle">
            Build and scale your Global Capability Center with our comprehensive
            GCC suite of services.
          </p>
        </div>

        <div className="content-layout">
          {/* Main Content Area */}
          <div className="service-main-content">
            <div className="service-overview">
              <h2>Transform Your Global Operations</h2>
              <p className="section-intro">
                In today's competitive landscape, establishing a Global Capability Center (GCC) in India isn't just about
                cost reduction — it's about building strategic capabilities that drive innovation and growth for the parent Organization. Our comprehensive
                GCC-as-a-Service offering takes you from concept to fully operational center in record time.
              </p>
              <p>
                Whether you're a Fortune 500 company looking to establish your first GCC or scaling existing operations,
                we provide end-to-end services that ensure success from day one.
              </p>
            </div>

            <div className="content-section">
              <h2>Why GCCs in India — Market trend</h2>
              <p>
                India has emerged as the global leader for GCC operations, with over 1,800+ centers and $50+ billion in
                annual revenue. The market advantages are compelling.
              </p>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">
                    <Users size={24} strokeWidth={1.5} />
                  </div>
                  <h4>Talent Abundance</h4>
                  <p>Talent pool of 6+ million technical professionals. India produces 1.5 million new engineering graduates annually.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <DollarSign size={24} strokeWidth={1.5} />
                  </div>
                  <h4>Cost Efficiency</h4>
                  <p>GCCs enable 40-60% cost savings while maintaining higher standards and better outcomes.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <TrendingUp size={24} strokeWidth={1.5} />
                  </div>
                  <h4>Innovation Ecosystem</h4>
                  <p>GCCs cross leverage India's high quality educational institutions, and startup ecosystem for both speed and innovation.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Engagement Models</h2>
              <div className="gcc-service-engagement-models-table">
                <div className="gcc-service-models-header">
                  <div className="gcc-service-model-column gcc-service-empty-header"></div>
                  <div className="gcc-service-model-column">
                    <div className="gcc-service-popular-tag">Popular</div>
                    <h4>Managed GCC</h4>
                    <p className="gcc-service-model-subtitle">Design-Build-Operate-Transform-Transfer (DBOTT)</p>
                  </div>
                  <div className="gcc-service-model-column">
                    <h4>PEO/EOR Model</h4>
                    <p className="gcc-service-model-subtitle">Professional Employer Organization</p>
                  </div>
                  <div className="gcc-service-model-column">
                    <h4>Project/Demand Based</h4>
                    <p className="gcc-service-model-subtitle">Time-bound Engagements</p>
                  </div>
                </div>

                <div className="gcc-service-models-row">
                  <div className="gcc-service-row-header">Scope</div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Discover: Vision and strategic alignment</li>
                      <li>Design: Organization cross-functional and global/matrix structure</li>
                      <li>Establish: Operational playbook</li>
                      <li>Hire: Global hiring process and standard alignment</li>
                      <li>Enable: Build own or shared office space</li>
                    </ul>
                  </div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Establish: Operational playbook (employee salary, tax, compliance, etc.)</li>
                      <li>Hire: Global hiring process and standard alignment</li>
                      <li>Enable: Build own or shared office space</li>
                    </ul>
                  </div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Fixed bid or T&M based engagement</li>
                      <li>Time bound or temporary assignments</li>
                      <li>Driven by SOW or change requests</li>
                      <li>Prototype or Proof of concepts</li>
                      <li>Discovery or research work</li>
                    </ul>
                  </div>
                </div>

                <div className="gcc-service-models-row">
                  <div className="gcc-service-row-header">Suitable For</div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Leadership sees GCC as strategically important</li>
                      <li>GCCs are planning for more than 30 people in the India center</li>
                    </ul>
                  </div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Quick start to test and try if GCC truly works before scaling big</li>
                      <li>To validate how India's cost advantage is seen in books</li>
                      <li>Planning for less than 30 people in India</li>
                    </ul>
                  </div>
                  <div className="gcc-service-model-content">
                    <ul>
                      <li>Need of SME engagement on short term or time-based assignments (ex: AI or Cloud or process excellence)</li>
                      <li>One off projects/initiatives like due diligence, cloud cost optimization, Tools consolidation, etc.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Cospin's GCC Playbooks — Complete Framework</h2>
              <p>Our battle-tested playbooks accelerate your GCC setup and ensure operational excellence from day one.</p>
              <div className="playbooks-grid">
                <div className="playbook-card">
                  <h4>Strategy Definition</h4>
                  <p>Comprehensive strategic foundation: Purpose, Scale, Functions, KPIs.</p>
                </div>
                <div className="playbook-card">
                  <h4>Glocalize</h4>
                  <p>Integration Guides: Aligning India operations with HQ culture, policies and avoid operational silos.</p>
                </div>
                <div className="playbook-card">
                  <h4>Recruitment & Onboarding</h4>
                  <p>End-to-end talent integration: From hiring to cultural orientation to going live.</p>
                </div>
                <div className="playbook-card">
                  <h4>Local Governance</h4>
                  <p>Timely audits, Record keeping and Local laws compliance.</p>
                </div>
                <div className="playbook-card">
                  <h4>IT & Security Frameworks</h4>
                  <p>Secure operations: Match the parent organization's IT & Security standards in addition to local requirements.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Talent Strategy — Three Strategic Pillars</h2>
              <p className="section-intro">
                Our comprehensive talent strategy ensures you build, develop, and retain world-class teams that drive your GCC's success.
              </p>
              <div className="pillars-grid">
                <div className="pillar-card">
                  <div className="pillar-header">
                    <div className="pillar-icon">
                      <Users size={28} strokeWidth={1.5} />
                    </div>
                    <h4>Talent Acquisition</h4>
                  </div>
                  <p>Data-driven hiring and sourcing for niche roles. Partnerships with India's top recruitment ecosystems. Strategic city playbooks covering major tech hubs. Role design for ownership and retention. AI-assisted hiring processes.</p>
                </div>
                <div className="pillar-card">
                  <div className="pillar-header">
                    <div className="pillar-icon">
                      <Target size={28} strokeWidth={1.5} />
                    </div>
                    <h4>Talent Development</h4>
                  </div>
                  <p>Continuous learning programs for tech talent. Upskilling frameworks for future readiness. Partnership with high quality education institutions. Bespoke L&D programs for niche roles. Leadership labs for first-time managers.</p>
                </div>
                <div className="pillar-card">
                  <div className="pillar-header">
                    <div className="pillar-icon">
                      <Award size={28} strokeWidth={1.5} />
                    </div>
                    <h4>Talent Retention</h4>
                  </div>
                  <p>Custom made retention programs for location and roles. Exclusive talent retention programs for women. Purpose-driven EVP aligned with impact. Well established rewards and recognition framework. Career architecture with clear growth paths.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Success Snapshot — Proven Results</h2>
              <div className="benefits-grid">
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <Users size={24} strokeWidth={1.5} />
                  </div>
                  <h4>200+ Members</h4>
                  <p>Built a tech GCC completely from scratch to 200+ members in just 21 months</p>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <DollarSign size={24} strokeWidth={1.5} />
                  </div>
                  <h4>55% Cost Reduction</h4>
                  <p>Reduced R&D spend of a mid-tier SaaS org by 55% in less than 2 years</p>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <Rocket size={24} strokeWidth={1.5} />
                  </div>
                  <h4>18-Month Transition</h4>
                  <p>Transitioned a US HQ MarTech GCC from PEO to DBOTT model in 18 months</p>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <Award size={24} strokeWidth={1.5} />
                  </div>
                  <h4>90%+ Retention</h4>
                  <p>Achieved 90% talent retention for tech roles of Managed GCCs</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default GCCService;

